import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useForm, useFormState } from 'react-final-form';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import FiltersHeader from '../../../NewSearch/common/FiltersHeader';
import BinaModal from '../../../ReactModal';
import useSearchModal from '../../../../hooks/useSearchModal';
import {
  entitiesFilter,
  setCoordinates
} from '../../../../utils/helpers/common';
import {
  hidePopupHeader,
  showPopupHeader,
  setError
} from '../../../../actions';
import { NewSearchEmpty } from '../../../NewSearch/NewSearchCities/NewSearchEmpty';
import Loader from '../../../Loader';
import { NewRadioButton } from '../../../NewRadioButton';
import { getTownships } from '../../../../api/queries';
import { FORM_VALUES } from '../../../../utils/helpers/constants';
import { Input, InputTypeMap } from '../../../ui/Input';

const PopupVillage = ({ isOpen, closeHandler, setInitCoordinates }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { change } = useForm();
  const { values } = useFormState();
  const inputEl = useRef(null);
  const {
    isFocused,
    searchString,
    onBlurHandler,
    onFocusHandler,
    onChangeHandler,
    setIsSubmittedForm,
    defaultCancelHandler
  } = useSearchModal(inputEl);

  const regionId = values[FORM_VALUES.locationId];
  const currentVillageId = values[FORM_VALUES.villageId];
  const villageQueryVariables = { regionId };
  const { data, loading, error } = useQuery(getTownships, {
    variables: villageQueryVariables
  });

  useEffect(() => {
    if (error) dispatch(setError(true));
  }, [error]);

  const cancelHandler = () => {
    dispatch(showPopupHeader());
    defaultCancelHandler();
  };

  const onFocusInputHandler = () => {
    dispatch(hidePopupHeader());
    onFocusHandler();
  };

  const onChangeRadioHandler = e => {
    const villageId = e.target.value;
    change(FORM_VALUES.villageId, villageId);
    const villageData = data.townships.find(
      village => village.id === villageId
    );
    change(FORM_VALUES.villageName, villageData?.name.trim());
    setCoordinates(villageData, setInitCoordinates);
    dispatch(showPopupHeader());
    closeHandler();
    cancelHandler();
  };

  const handleKeyDown = e => {
    if (e.keyCode === 13 && e.shiftKey === false) {
      e.preventDefault();
      setIsSubmittedForm(true);
    }
  };

  const villagesList =
    (!isFocused || searchString) &&
    entitiesFilter(data?.townships || [], searchString).map(township => (
      <NewRadioButton
        data={township}
        key={township.id}
        cypress="location"
        name="select-township"
        currentValue={currentVillageId}
        onChangeHandler={onChangeRadioHandler}
      />
    ));

  const emptySearch = !villagesList.length && searchString && (
    <NewSearchEmpty
      onClickHandler={cancelHandler}
      linkText={t('new_ad.return_village')}
    />
  );

  return (
    <BinaModal
      isOpen={isOpen}
      modalId="village"
      onRequestClose={closeHandler}
      contentClassName="new-search-popup"
      animation="bottom"
      isWrapperWithScroll={false}
    >
      <FiltersHeader title={t('new_ad.village')} close={closeHandler} />
      <div style={{ overflowY: 'scroll', height: '100%' }}>
        <div
          className={classNames('new-search-popup__tools', {
            'new-search-popup__tools--is-focused': isFocused
          })}
        >
          <Input
            refEl={inputEl}
            value={searchString}
            onKeyDown={handleKeyDown}
            type={InputTypeMap.search}
            onBlurHandler={onBlurHandler}
            onChangeHandler={onChangeHandler}
            onFocusHandler={onFocusInputHandler}
            placeholder={t('new_ad.find_village')}
            isInsideModalWithAnimation
          />
          {isFocused && (
            <button
              className="new-search-popup__content-btn new-search-popup__content-btn--cancel"
              onClick={cancelHandler}
            >
              {t('common.cancel')}
            </button>
          )}
        </div>
        <div className={'new-search-popup__container'}>
          <Loader loading={loading}>{villagesList}</Loader>
          {emptySearch}
        </div>
      </div>
    </BinaModal>
  );
};

PopupVillage.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeHandler: PropTypes.func.isRequired,
  setInitCoordinates: PropTypes.func.isRequired
};

export default PopupVillage;
